import { useEffect, useState } from "react";
import { RotateCcw, HelpCircle } from "lucide-react";
import { api } from "../lib/api";
import { TEACHING_CONTENT } from "../lib/teaching-content";
import { restoreAllTeachingBanners } from "../components/TeachingBanner";

/** Help center (/settings/help). Lists every teaching banner so the
 *  user can re-see any dismissed one, plus a single restore-all. */
export function TeachingHelp() {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.teachingDismissed();
      setDismissed(new Set(r.dismissed ?? []));
    } catch {
      /* best effort */
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const restoreOne = async (id: string) => {
    await api.teachingRestore(id);
    refresh();
  };
  const restoreAll = async () => {
    setBusy(true);
    try {
      await restoreAllTeachingBanners();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const entries = Object.entries(TEACHING_CONTENT);

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <header className="space-y-1">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
          Settings
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Help and tips
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          Every short explainer Memso shows around the app. Restore any you
          dismissed, or bring them all back.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={restoreAll}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md border border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/40 disabled:opacity-50 text-xs font-semibold px-3 py-1.5"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Show all banners again
        </button>
        <button
          type="button"
          disabled
          title="Coming soon"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 text-xs font-semibold px-3 py-1.5 cursor-not-allowed"
        >
          Guided tour (coming soon)
        </button>
      </div>

      <ul className="space-y-2">
        {entries.map(([id, e]) => {
          const isOff = dismissed.has(id);
          return (
            <li
              key={id}
              className="glass rounded-lg border border-slate-200/70 dark:border-slate-700/50 p-3 flex items-start gap-3"
            >
              <HelpCircle className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {e.title}
                  <span className="ml-2 text-[10px] font-mono text-slate-400 dark:text-slate-500">
                    {id}
                  </span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {e.body}
                </div>
              </div>
              {isOff ? (
                <button
                  type="button"
                  onClick={() => restoreOne(id)}
                  className="flex-shrink-0 text-[11px] font-semibold rounded-md border border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/40 px-2 py-1"
                >
                  Restore
                </button>
              ) : (
                <span className="flex-shrink-0 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  Visible
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
